import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import {
  fetchEmsGovernancePolicyApi,
  updateEmsGovernancePolicyApi,
} from "../../lib/api";

function toCsv(arr) {
  return Array.isArray(arr) ? arr.join(", ") : "";
}

function formatPolicyValue(value) {
  return String(value || "-")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatPolicyList(values) {
  const items = Array.isArray(values) ? values : [];
  return items.length ? items.map(formatPolicyValue).join(", ") : "-";
}

function fromCsv(text) {
  return String(text || "")
    .split(",")
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
}

export default function EMSGovernancePolicy() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);
  const role = String(localStorage.getItem("role") || "").toLowerCase();
  const canEdit = role === "admin";

  const [severityScale, setSeverityScale] = useState("");
  const [riskScoringScale, setRiskScoringScale] = useState("");
  const [legalCategories, setLegalCategories] = useState("");
  const [approvalDepthDefault, setApprovalDepthDefault] = useState("two_step");
  const [rejectedItemPolicy, setRejectedItemPolicy] = useState("must_resubmit");
  const [escalationRoute, setEscalationRoute] = useState("email_plus_admin_digest");
  const [unassignedOwnerDefault, setUnassignedOwnerDefault] = useState("EMS Coordinator");
  const [clientVisible, setClientVisible] = useState(false);

  const loadPolicy = async () => {
    try {
      setLoading(true);
      const data = await fetchEmsGovernancePolicyApi();
      setPolicy(data || null);
      setSeverityScale(toCsv(data?.severity_scale));
      setRiskScoringScale(toCsv(data?.risk_scoring_scale));
      setLegalCategories(toCsv(data?.legal_categories));
      setApprovalDepthDefault(String(data?.approval_depth_default || "two_step"));
      setRejectedItemPolicy(String(data?.rejected_item_policy || "must_resubmit"));
      setEscalationRoute(String(data?.escalation_route || "email_plus_admin_digest"));
      setUnassignedOwnerDefault(String(data?.unassigned_owner_default || "EMS Coordinator"));
      setClientVisible(Boolean(data?.client_visible));
    } catch (err) {
      toast.error(err?.message || "Failed to load governance policy");
      setPolicy(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicy();
  }, []);

  const summary = useMemo(() => {
    if (!policy) return [];
    return [
      { label: "Severity levels", value: (policy?.severity_scale || []).length },
      { label: "Risk score levels", value: (policy?.risk_scoring_scale || []).length },
      { label: "Legal categories", value: (policy?.legal_categories || []).length },
      { label: "Approval depth", value: String(policy?.approval_depth_default || "-") },
    ];
  }, [policy]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      toast.error("Only admin can update governance policy");
      return;
    }
    const payload = {
      severity_scale: fromCsv(severityScale),
      risk_scoring_scale: fromCsv(riskScoringScale),
      legal_categories: fromCsv(legalCategories),
      approval_depth_default: approvalDepthDefault,
      rejected_item_policy: rejectedItemPolicy,
      escalation_route: escalationRoute,
      unassigned_owner_default: unassignedOwnerDefault,
      client_visible: clientVisible,
    };
    try {
      setSaving(true);
      const updated = await updateEmsGovernancePolicyApi(payload);
      setPolicy(updated || null);
      toast.success("Governance policy updated");
    } catch (err) {
      toast.error(err?.message || "Failed to update governance policy");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h1>EMS Governance Policy</h1>
          <p className="subtitle">Manage controlled taxonomy registries and governance decisions used by workflows.</p>
        </div>
      </div>

      {canEdit ? (
        <>
          <div className="summary-row">
            {summary.map((it) => (
              <div className="kpi-card" key={it.label}>
                <div className="kpi-label">{it.label}</div>
                <h2>{loading ? "-" : it.value}</h2>
              </div>
            ))}
          </div>

          <div className="card settings-card">
            <div className="settings-header">
              <h3>Policy Configuration</h3>
              <span className="kpi-sub">Applies to EMS modules and stage-readiness workflow</span>
            </div>
            <form onSubmit={handleSave}>
              <div className="settings-grid">
                <label className="settings-field">
                  <span>Severity scale (comma-separated)</span>
                  <input value={severityScale} onChange={(e) => setSeverityScale(e.target.value)} disabled={saving} />
                </label>
                <label className="settings-field">
                  <span>Risk scoring scale (comma-separated)</span>
                  <input value={riskScoringScale} onChange={(e) => setRiskScoringScale(e.target.value)} disabled={saving} />
                </label>
                <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Legal categories (comma-separated)</span>
                  <input value={legalCategories} onChange={(e) => setLegalCategories(e.target.value)} disabled={saving} />
                </label>
                <label className="settings-field">
                  <span>Approval depth default</span>
                  <select value={approvalDepthDefault} onChange={(e) => setApprovalDepthDefault(e.target.value)} disabled={saving}>
                    <option value="one_step">one_step</option>
                    <option value="two_step">two_step</option>
                  </select>
                </label>
                <label className="settings-field">
                  <span>Rejected item policy</span>
                  <select value={rejectedItemPolicy} onChange={(e) => setRejectedItemPolicy(e.target.value)} disabled={saving}>
                    <option value="must_resubmit">must_resubmit</option>
                    <option value="can_close">can_close</option>
                  </select>
                </label>
                <label className="settings-field">
                  <span>Escalation route</span>
                  <select value={escalationRoute} onChange={(e) => setEscalationRoute(e.target.value)} disabled={saving}>
                    <option value="email_only">email_only</option>
                    <option value="email_plus_admin_digest">email_plus_admin_digest</option>
                  </select>
                </label>
                <label className="settings-field">
                  <span>Default unassigned owner</span>
                  <input value={unassignedOwnerDefault} onChange={(e) => setUnassignedOwnerDefault(e.target.value)} disabled={saving} />
                </label>
                <label className="governance-client-visibility" style={{ gridColumn: "1 / -1" }}>
                  <input
                    type="checkbox"
                    checked={clientVisible}
                    onChange={(e) => setClientVisible(e.target.checked)}
                    disabled={saving}
                  />
                  <span>
                    <strong>Share configured governance record with the client</strong>
                    <small>Clients can view the agreed settings, but cannot change them.</small>
                  </span>
                </label>
              </div>
              <div className="settings-actions">
                <button className="btn-primary" type="submit" disabled={saving || loading}>
                  {saving ? "Saving..." : "Save governance policy"}
                </button>
              </div>
            </form>
          </div>
        </>
      ) : policy?.client_visible ? (
        <div className="card settings-card governance-client-card">
          <div className="settings-header">
            <h3>Agreed Governance Configuration</h3>
            <span className="governance-status">Configured</span>
          </div>
          <p className="governance-record-intro">This record has been agreed with your platform administrator. It is available for reference and audit purposes.</p>
          <div className="governance-record-grid">
            <div className="governance-record-field"><span>Severity scale</span><strong>{formatPolicyList(policy?.severity_scale)}</strong></div>
            <div className="governance-record-field"><span>Risk scoring scale</span><strong>{formatPolicyList(policy?.risk_scoring_scale)}</strong></div>
            <div className="governance-record-field governance-record-wide"><span>Legal categories</span><strong>{formatPolicyList(policy?.legal_categories)}</strong></div>
            <div className="governance-record-field"><span>Approval depth</span><strong>{formatPolicyValue(policy?.approval_depth_default)}</strong></div>
            <div className="governance-record-field"><span>Rejected item policy</span><strong>{formatPolicyValue(policy?.rejected_item_policy)}</strong></div>
            <div className="governance-record-field"><span>Escalation route</span><strong>{formatPolicyValue(policy?.escalation_route)}</strong></div>
            <div className="governance-record-field"><span>Default unassigned owner</span><strong>{policy?.unassigned_owner_default || "-"}</strong></div>
          </div>
        </div>
      ) : (
        <div className="card settings-card governance-client-card">
          <div className="settings-header">
            <h3>Governance Configuration</h3>
            <span className="governance-status">Admin-managed</span>
          </div>
          <div className="governance-client-message">
            <h4>To be agreed with your platform administrator</h4>
            <p>
              Hindsight Sustainability configures these settings with you during implementation and reviews them when your requirements change.
            </p>
            <p>
              This includes risk scoring, severity definitions, legal categories, approvals, and escalation routes. Contact your platform administrator if you need to review or amend them.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
